import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Shield, Settings, Palette, FileText, 
  HelpCircle, Link, ChevronRight, Clock, DollarSign,
  Briefcase, UserCheck, BookOpen, Activity, BarChart3,
  Target, Building2, Download
} from 'lucide-react';
import { api } from '../api/client';
import ExportHistoryPanel from '../components/ExportHistoryPanel';
import { useAuth } from '../context/AuthContext';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { tenantId: authTenantId } = useAuth();
  const tenantId = localStorage.getItem('impersonated_tenant_id') || authTenantId;
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const [usersRes, auditRes, consultingRes] = await Promise.all([
        api.getAdminUsers(),
        api.getAuditLogs({ limit: 5 }),
        api.getConsultingStats().catch(() => ({ data: {} }))
      ]);
      setStats({
        userCount: usersRes.data.total || 0,
        recentActivity: auditRes.data.logs || [],
        proposals: consultingRes.data?.totalProposals || 0,
        projectsConverted: consultingRes.data?.projectsConverted || 0
      });
    } catch (error) {
      console.error('Failed to load admin stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Section categories with items
  const adminCategories = [
    {
      title: 'User & Access Management',
      items: [
        { 
          key: 'users', 
          title: 'User Management', 
          description: 'Invite users, assign roles, manage access',
          icon: Users,
          path: '/admin/users',
          color: 'bg-blue-500'
        },
        { 
          key: 'roles', 
          title: 'Roles & Permissions', 
          description: 'Configure role permissions and access matrix',
          icon: Shield,
          path: '/admin/roles-permissions',
          color: 'bg-purple-500'
        },
        { 
          key: 'personas', 
          title: 'Personas', 
          description: 'Manage user and tenant personas',
          icon: UserCheck,
          path: '/admin/personas',
          color: 'bg-indigo-500',
          badge: 'New'
        }
      ]
    },
    {
      title: 'Consulting Suite',
      items: [
        { 
          key: 'consulting', 
          title: 'Consulting Suite Admin', 
          description: 'Proposal Builder, Planning, Rate Cards, Templates',
          icon: Briefcase,
          path: '/admin/consulting-suite',
          color: 'bg-purple-600',
          badge: 'New'
        }
      ]
    },
    {
      title: 'System Configuration',
      items: [
        { 
          key: 'tenant-setup', 
          title: 'Tenant Setup', 
          description: 'Currency, locale, working hours, fiscal year',
          icon: Building2,
          path: '/admin/tenant-setup',
          color: 'bg-indigo-600',
          badge: 'New'
        },
        { 
          key: 'config', 
          title: 'Configuration', 
          description: 'Status legend, thresholds, escalation levels',
          icon: Settings,
          path: '/admin/configuration',
          color: 'bg-gray-600'
        },
        { 
          key: 'brand', 
          title: 'Brand & Theming', 
          description: 'Logo, colors, report headers/footers',
          icon: Palette,
          path: '/admin/brand',
          color: 'bg-pink-500'
        },
        { 
          key: 'integrations', 
          title: 'Integrations', 
          description: 'SSO, Jira, Zendesk, Slack connectors',
          icon: Link,
          path: '/admin/integrations',
          color: 'bg-teal-500'
        }
      ]
    },
    {
      title: 'Monitoring & Support',
      items: [
        { 
          key: 'audit', 
          title: 'Audit Logs', 
          description: 'Track user actions and system events',
          icon: FileText,
          path: '/admin/audit',
          color: 'bg-orange-500'
        },
        { 
          key: 'analytics', 
          title: 'Analytics & Usage', 
          description: 'Platform usage metrics and insights',
          icon: BarChart3,
          path: '/admin/analytics',
          color: 'bg-cyan-500',
          badge: 'New'
        },
        { 
          key: 'support', 
          title: 'Help & Support', 
          description: 'Documentation, tickets, feedback',
          icon: HelpCircle,
          path: '/admin/support',
          color: 'bg-emerald-500'
        }
      ]
    }
  ];

  // Format recent activity for display
  const formatActivityAction = (action) => {
    const actionLabels = {
      'proposal_created': 'Created Proposal',
      'proposal_updated': 'Updated Proposal',
      'proposal_exported': 'Exported Proposal',
      'proposal_converted': 'Converted to Project',
      'plan_updated': 'Updated Plan',
      'rate_card_modified': 'Modified Rate Card',
      'user_created': 'Created User',
      'user_updated': 'Updated User',
      'permission_changed': 'Changed Permission',
      'login': 'User Login',
      'logout': 'User Logout'
    };
    return actionLabels[action] || action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown Action';
  };

  const getActivityIcon = (action) => {
    if (action?.includes('proposal')) return <Briefcase className="w-4 h-4 text-purple-500" />;
    if (action?.includes('user')) return <Users className="w-4 h-4 text-blue-500" />;
    if (action?.includes('permission')) return <Shield className="w-4 h-4 text-orange-500" />;
    if (action?.includes('rate_card')) return <DollarSign className="w-4 h-4 text-green-500" />;
    return <Activity className="w-4 h-4 text-gray-500" />;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
          <Settings className="w-7 h-7 text-gray-700" />
          Administration
        </h1>
        <p className="text-[var(--clr-text-secondary)] mt-1">System settings, user management, and configuration</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[var(--clr-surface-card)] rounded-xl border p-5 shadow-[var(--shadow-card)]">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-[var(--clr-text-primary)]">{stats.userCount || 0}</p>
          <p className="text-sm text-[var(--clr-text-secondary)]">Total Users</p>
        </div>
        <div className="bg-[var(--clr-surface-card)] rounded-xl border p-5 shadow-[var(--shadow-card)]">
          <div className="flex items-center justify-between mb-2">
            <Shield className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-3xl font-bold text-[var(--clr-text-primary)]">12</p>
          <p className="text-sm text-[var(--clr-text-secondary)]">Defined Roles</p>
        </div>
        <div className="bg-[var(--clr-surface-card)] rounded-xl border p-5 shadow-[var(--shadow-card)]">
          <div className="flex items-center justify-between mb-2">
            <Briefcase className="w-5 h-5 text-purple-500" />
          </div>
          <p className="text-3xl font-bold text-[var(--clr-text-primary)]">{stats.proposals || 0}</p>
          <p className="text-sm text-[var(--clr-text-secondary)]">Proposals Created</p>
        </div>
        <div className="bg-[var(--clr-surface-card)] rounded-xl border p-5 shadow-[var(--shadow-card)]">
          <div className="flex items-center justify-between mb-2">
            <Target className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-[var(--clr-text-primary)]">{stats.projectsConverted || 0}</p>
          <p className="text-sm text-[var(--clr-text-secondary)]">Projects Converted</p>
        </div>
      </div>

      {/* Admin Sections by Category */}
      {adminCategories.map((category) => (
        <div key={category.title} className="space-y-3">
          <h2 className="text-sm font-semibold text-[var(--clr-text-secondary)] uppercase tracking-wider">
            {category.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {category.items.map((section) => {
              const Icon = section.icon;
              return (
                <div
                  key={section.key}
                  onClick={() => navigate(section.path)}
                  className="bg-[var(--clr-surface-card)] rounded-xl border p-5 shadow-[var(--shadow-card)] hover:shadow-md cursor-pointer transition-all group"
                  data-testid={`admin-section-${section.key}`}
                >
                  <div className="flex items-start justify-between">
                    <div className={`p-3 rounded-lg ${section.color}`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex items-center gap-2">
                      {section.badge && (
                        <span className="px-2 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 rounded-full">
                          {section.badge}
                        </span>
                      )}
                      <ChevronRight className="w-5 h-5 text-[var(--clr-text-muted)] group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                  <h3 className="mt-4 font-semibold text-[var(--clr-text-primary)]">{section.title}</h3>
                  <p className="mt-1 text-sm text-[var(--clr-text-secondary)]">{section.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Recent Activity */}
      <div className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)]">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[var(--clr-text-primary)] flex items-center gap-2">
            <Clock className="w-5 h-5 text-[var(--clr-text-secondary)]" />
            Recent Activity
          </h2>
          <button 
            onClick={() => navigate('/admin/audit')}
            className="text-sm text-[var(--clr-brand-primary)] hover:underline"
          >
            View All
          </button>
        </div>
        <div className="divide-y">
          {stats.recentActivity?.length > 0 ? stats.recentActivity.slice(0, 5).map((log, idx) => (
            <div key={idx} className="p-4 hover:bg-[var(--clr-surface-hover)]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getActivityIcon(log.action)}
                  <div>
                    <p className="font-medium text-[var(--clr-text-primary)]">{formatActivityAction(log.action)}</p>
                    <p className="text-sm text-[var(--clr-text-muted)]">{log.resource_type} • {log.user_name || 'System'}</p>
                  </div>
                </div>
                <span className="text-xs text-[var(--clr-text-muted)]">
                  {new Date(log.timestamp).toLocaleString()}
                </span>
              </div>
            </div>
          )) : (
            <div className="p-8 text-center text-[var(--clr-text-muted)]">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p>No recent activity</p>
            </div>
          )}
        </div>
      </div>

      {/* Export History */}
      {tenantId && (
        <ExportHistoryPanel 
          tenantId={tenantId} 
          showStats={true} 
          compact={true}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
